import React, { useState } from "react";
import {
  Table,
  Input,
  Button,
  Upload,
  Modal,
  Tag,
  Empty,
  Dropdown,
  Spin,
} from "antd";
import {
  SearchOutlined,
  UploadOutlined,
  FilterFilled,
  FileImageFilled,
  ReloadOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useCallsTable } from "../../../hooks";
import { PAGE_URL } from "../../../utils";
import FilterMenu from "../../../components/filter-menu/FilterMenu";
import { rangeOptions, sentimentScaleDefinition } from "./callsDatas";
import DateRangePicker from "../../../components/DateRangePicker";
import { AppPagination } from "../../../components";
import "./callTable.scss";
import { callsApiService } from "../../../api/calls";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";

export const CallsTable: React.FC<{ isUploadButton: boolean }> = ({
  isUploadButton = true,
}) => {
  const navigate = useNavigate();
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [isBulkDownloading, setIsBulkDownloading] = useState(false);

  const {
    isModalVisible,
    file,
    callsData,
    loading,
    search,
    totalRecords,
    page,
    pageSize,
    pageSizeOptions,
    topics,
    dateRange,
    timezone,
    rangeType,
    sentimentsFilter,
    statusFilter,
    statusClassificationFilter,
    handleSearchChange,
    handleDateRangeChange,
    handleApplyFilterOnSentiments,
    handleCancelFilterOnSentiments,
    handleApplyFilterOnStatusClassification,
    handleCancelFilterOnStatusClassification,
    handleApplyFilterOnTopics,
    handleCancelFilterOnTopics,
    handleApplyFilterOnStatus,
    handleCancelFilterOnStatus,
    handleUpload,
    showModal,
    handleCancel,
    beforeUpload,
    onPaginationChange,
    fetchCallsData,
    handleTableChange,
  } = useCallsTable();

  const handleBulkDownload = async () => {
    setIsBulkDownloading(true); 
    const selectedCallsInfo = selectedRows.map((row) => ({
      callId: row.callId,
      fileName: row.fileName,
    }));

    try {
      const res = await callsApiService.bulkDownloadOutput(selectedCallsInfo);

      if (res.data) {
        const link = document.createElement("a");
        link.href = res.data;
        link.setAttribute("download", "bulk_download.zip");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        console.error("Failed to generate ZIP file.");
      }
    } catch (error) {
      console.error("Bulk download failed:", error);
    } finally {
      setIsBulkDownloading(false); 
      setSelectedRows([]); 
    }
  };

  const dataWithUniqueKeys =
    callsData?.map((row, index) => ({
      ...row,
      uniqueKey: `${row.callId}-${row.fileName}-${index}`,
    })) || [];

  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[], selectedRows: any[]) => {
      setSelectedRows(selectedRows);
    },
    getCheckboxProps: (record: any) => ({
      disabled:
        record.status !== "completed" ||
        record.statusClassification !== "SUCCESS", 
    }),
  };

  const handleTableRangeTypeChange = () => {};

  // Table columns configuration
  const columns: any = [
    {
      title: "File Name",
      dataIndex: "fileName",
      key: "fileName",
      sorter: true,
      className: "w-10 cursor-pointer",
      render: (fileName: string, record: any) => (
        <div
          onClick={() =>
            navigate(
              `${PAGE_URL.DASHBOARD}${PAGE_URL.CALL_INSIGHTS}/${
                record.callId
              }?fileName=${encodeURIComponent(record.fileName)}`
            )
          }
        >
          <span className="text-dash">
            <FileImageFilled />
          </span>
          <span
            className="text-dash"
            style={{ marginLeft: "5px", color: "blue", fontSize: "11px" }}
          >
            {fileName}
          </span>
        </div>
      ),
    },
    {
      title: "Call ID",
      dataIndex: "callId",
      key: "callId",
      sorter: true,
      className: "w-10",
      render: (callId: string) => <span className="text-dash" style={{fontSize : '10px'}} >{callId}</span>,
    },
    {
      title: (
        <div className="drop-down-demo">
          <Dropdown
            overlay={
              <FilterMenu
                title="Filter By Sentiments"
                options={["Positive", "Neutral", "Negative"]}
                onApply={handleApplyFilterOnSentiments}
                onCancel={handleCancelFilterOnSentiments}
                styles={{ marginLeft: "60px", maxWidth: "190px" }}
                showCancel={true}
              />
            }
            trigger={["click"]}
            placement="bottomRight"
          >
            <div className="flex jcsb">
              <span>Sentiments (avg)</span>
              <FilterFilled />
            </div>
          </Dropdown>
        </div>
      ),
      dataIndex: "sentimentScore",
      key: "sentimentScore",
      className: "w-15",
      render: (sentiment: number) => {
        if (sentiment === null) return <span>N/A</span>;
        const sentimentLabel =
          sentimentScaleDefinition[
            sentiment as unknown as keyof typeof sentimentScaleDefinition
          ] || "Unknown";
        const colorClass = `badge-${
          sentiment === -1
            ? "negative"
            : sentiment === 0
            ? "neutral"
            : sentiment === 1
            ? "positive"
            : "default"
        }`;
        return (
          <span className={`custom-badge ${colorClass}`}>{sentimentLabel}</span>
        );
      },
    },
    {
      title: (
        <div className="drop-down-demo">
          <Dropdown
            overlay={
              <FilterMenu
                title="Filter By Status"
                options={["completed", "failed", "processing"]}
                onApply={handleApplyFilterOnStatus}
                onCancel={handleCancelFilterOnStatus}
                styles={{ marginLeft: "60px", maxWidth: "190px" }}
                showCancel={true}
              />
            }
            trigger={["click"]}
            placement="bottomRight"
          >
            <div className="flex jcsb">
              <p>Status</p>
              <FilterFilled />
            </div>
          </Dropdown>
        </div>
      ),
      dataIndex: "status",
      key: "status",
      className: "w-10",
      align: "center",
      render: (status: string) => {
        const statusConfig: Record<string, { color: string; label: string }> = {
          completed: { color: "green", label: "Completed" },
          failed: { color: "red", label: "Failed" },
          processing: { color: "orange", label: "Processing" },
        };
        const statusInfo = statusConfig[status] || {
          color: "gray",
          label: "Unknown",
        };
        return <Tag color={statusInfo.color}>{statusInfo.label}</Tag>;
      },
    },
    {
      title: (
        <div className="drop-down-demo">
          <Dropdown
            overlay={
              <FilterMenu
                title="Status Classification Filter"
                options={[
                  "SUCCESS",
                  "CANDIDATE_ABSENCE_ISSUE",
                  "RATE_LIMITTING_ERROR",
                  "PARTIALLY_PROCESSED",
                  "UNABLE_TO_PROCESS"
                ]}
                onApply={handleApplyFilterOnStatusClassification}
                onCancel={handleCancelFilterOnStatusClassification}
                styles={{ marginLeft: "60px", maxWidth: "190px" }}
                showCancel={true}
              />
            }
            trigger={["click"]}
            placement="bottomRight"
          >
            <div className="flex jcsb">
              <p style={{ fontSize: "12px" }}>Status Classification</p>
              <FilterFilled />
            </div>
          </Dropdown>
        </div>
      ),
      key: "statusClassification",
      dataIndex: "statusClassification",
      align: "center",
      className: "w-15",
      render: (status: string) => {
        let finalStatus = "";
        let color = "black";
        switch (status) {
          case "CANDIDATE_ABSENCE_ISSUE":
            finalStatus = "ABSENCE OF CANDIDATE";
            color = "orange";
            break;
          case "RATE_LIMITTING_ERROR":
            finalStatus = "RATE LIMITTING ERROR";
            color = "red";
            break;
          case "SUCCESS":
            finalStatus = "SUCCESS";
            color = "green";
            break;
          case "PARTIALLY_PROCESSED":
            finalStatus = "PARTIALLY_PROCESSED";
            color = "blue";
            break;
          case "UNABLE_TO_PROCESS":
            finalStatus = "UNABLE_TO_PROCESS";
            color = "violet";
            break;
          default:
            finalStatus = "";
            color = "gray";
        }
        return <h6 style={{ color }}>{finalStatus}</h6>;
      },
    },
    {
      title: (
        <div className="drop-down-demo">
          <Dropdown
            overlay={
              <FilterMenu
                title="Filter By Topics"
                options={topics}
                onApply={handleApplyFilterOnTopics}
                onCancel={handleCancelFilterOnTopics}
                styles={{ marginLeft: "540px", maxWidth: "190px" }}
                showCancel={true}
              />
            }
            trigger={["click"]}
            placement="bottomRight"
          >
            <div className="flex jcsb">
              <p>Topics</p>
              <FilterFilled />
            </div>
          </Dropdown>
        </div>
      ),
      dataIndex: "topics",
      key: "topics",
      className: "w-20",
      render: (topics: string[]) => {
        if (!topics || topics.length === 0) return null;
        const visibleTopics = topics.slice(0, 2);
        const hasMore = topics.length > 2;
        return (
          <div className="flex gap-1">
            {visibleTopics.map((topic, index) => (
              <span key={index} className="list-topics">
                {topic}
              </span>
            ))}
            {hasMore && <span className="list-topics">...</span>}
          </div>
        );
      },
    },
    {
      title: "Time Taken",
      key: "timeTaken",
      align: "center",
      dataIndex: "timeTaken",
      className: "w-10",
      render: (timeTaken: { minutes?: number; seconds?: number }) => {
        if (!timeTaken) return <p>N/A</p>;
        const formattedTime = [
          timeTaken.minutes ? `${timeTaken.minutes}m` : "",
          timeTaken.seconds ? `${timeTaken.seconds}s` : "",
        ]
          .filter(Boolean)
          .join(" ");
        return <p style={{ color: "red" }}>{formattedTime}</p>;
      },
    },
  ];

  return (
    <Spin spinning={isBulkDownloading || loading} tip="Processing...">
      <div className="transactions-container">
        {isUploadButton && (
          <div className="upload-button-container">
            <Button
              className="dash-outline"
              icon={<UploadOutlined />}
              onClick={showModal}
            >
              Upload CSV
            </Button>
          </div>
        )}

        <div className="header-section">
          <h2 className="section-title">Recent Transactions</h2>

          <Button
            className="outline"
            onClick={() =>
              fetchCallsData({
                dateRange,
                timezone,
                search,
                sentiments: sentimentsFilter,
                status: statusFilter,
                page,
                statusClassification: statusClassificationFilter,
              })
            }
          >
            {" "}
            <ReloadOutlined />{" "}
          </Button>

          <div className="search-controls">
            <Input
              placeholder="Search by Call ID or Topics..."
              suffix={<SearchOutlined style={{ color: "#5f3cab" }} />}
              style={{ width: "300px" }}
              value={search}
              className="input-dash"
              onChange={handleSearchChange}
            />

            <div className="date-range-container">
              <span>Date Range</span>
              <DateRangePicker
                defaultRangeType={rangeType}
                rangeOptions={rangeOptions}
                onRangeTypeChange={handleTableRangeTypeChange}
                onDateRangeChange={handleDateRangeChange}
              />
            </div>
            <div>
              <Button
                type="primary"
                className="dash-outline"
                disabled={selectedRows.length < 1}
                onClick={handleBulkDownload}
              >
                Bulk Download ({selectedRows.length})
              </Button>
            </div>
          </div>
        </div>

        <div className="doc-table">
          <Table
            rowSelection={{
              type: "checkbox",
              ...rowSelection,
            }}
            rowKey="uniqueKey"
            columns={columns}
            dataSource={dataWithUniqueKeys || []}
            pagination={false}
            loading={loading}
            onChange={handleTableChange}
            locale={{
              emptyText: <Empty description="No data available" />,
            }}
          />
          <div className="pagination-container">
            <AppPagination
              current={page}
              total={totalRecords}
              pageSize={pageSize}
              pageSizeOptions={pageSizeOptions}
              showSizeChanger={true}
              onChange={(updatedPage, updatedPageSize) => {
                onPaginationChange(updatedPage, updatedPageSize);
                fetchCallsData({
                  dateRange,
                  timezone,
                  search,
                  sentiments: sentimentsFilter,
                  status: statusFilter,
                  page: updatedPage,
                  statusClassification: statusClassificationFilter,
                });
              }}
            />
          </div>
        </div>

        <Modal
          title="Upload CSV File"
          visible={isModalVisible}
          onCancel={handleCancel}
          footer={null}
        >
          <Upload beforeUpload={beforeUpload} showUploadList={true} accept=".csv">
            <Button icon={<UploadOutlined />}>Select CSV File</Button>
          </Upload>
          <Button
            type="primary"
            onClick={handleUpload}
            loading={loading}
            disabled={!file}
            className="upload-submit-btn"
          >
            Upload
          </Button>
        </Modal>
      </div>
    </Spin>
  );
};